import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { AmenityData, amenityResolver } from '@/lib/validations/AmenitiesSchema';
import { i18n } from '@/utils/i18n';

import AddAnotherButton from '../buttons/AddAnotherButton';
import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';

interface AmenityFormProps {
  initialData?: AmenityData;
  showDoneButton: boolean;
  onAdd?: (data: AmenityData) => void;
  onDone?: (data: AmenityData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const AmenityForm = ({
  initialData,
  showDoneButton,
  onDone,
  onAdd,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: AmenityFormProps) => {
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    trigger,
    formState: { isValid, errors }
  } = useForm<AmenityData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: amenityResolver as any
  });

  const onSubmit: SubmitHandler<AmenityData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="title"
          control={control}
          placeholder="Enter name"
          returnKeyLabel="next"
          returnKeyType="next"
          label="Name"
        />

        {onAdd && (
          <AddAnotherButton
            onPress={() => {
              trigger().then((valid) => {
                if (valid) onAdd(getValues());
              });
            }}
            label={i18n.t('profile.create_form.submit_add_amenity')}
          />
        )}
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      {showDoneButton && (
        <BrandedButton loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {doneLabel}
        </BrandedButton>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default AmenityForm;
